import React, { useState } from 'react';
import moment from 'moment';
import { getPolyglot } from '../../../i18n';

import { DataGrid, GridColDef } from '@mui/x-data-grid';
import {
  Report,
  ReportQueryParams,
} from '../../../redux/reports/api/Report.model';
import ReportMenuActions from './ReportMenuActions';
import ReportAPI from '../../../redux/reports/api/ReportAPI';
import ReportDetailModal from './ReportDetail/ReportDetailModal';
import ReportFilters from './ReportFilters/ReportFilters';
import { TimeFrame } from '../../../redux/monitoring/api/monitoring-ui.model';
import { useAppSelector } from '../../../redux/store.model';
import { getMyWorkspaceIdSelector } from '../../../redux/stoerkID/selectors/StoerkId.selectors';
import { skipToken } from '@reduxjs/toolkit/query';
import useWatchError from '../../../handlingErrors/useWatchError';
import { serializeDate } from '../../../redux/reports/api/Report.util';
import _ from 'lodash';

interface ReportsTableProps {
  groupId?: string;
}
const ReportsTable = ({ groupId }: ReportsTableProps) => {
  const polyglot = getPolyglot();
  const workspaceId = useAppSelector(getMyWorkspaceIdSelector);

  const {
    data: reportTypes,
    isLoading: isLoadingReportTypes,
    ...rest
  } = ReportAPI.useGetReportTypesQuery(workspaceId ?? skipToken);

  const [paginationModel, setPaginationModel] = React.useState({
    page: 0,
    pageSize: 10,
  });
  const [timeFrame, setTimeFrame] = useState<TimeFrame | undefined>();
  const [reportTypeIdsSelected, setReportTypeIdsSelected] = useState<string[]>(
    reportTypes?.map((type) => type.id) || []
  );

  const paramsReport: ReportQueryParams = {
    groupId,
    workspaceId,
    page: paginationModel.page,
    pageSize: paginationModel.pageSize,
    reportTypeIds: reportTypeIdsSelected.length
      ? reportTypeIdsSelected
      : undefined,
    from: timeFrame?.start ? serializeDate(timeFrame.start) : undefined,
    to: timeFrame?.end ? serializeDate(timeFrame.end) : undefined,
  };
  const {
    data: reportsResponse,
    isFetching: isFetchingReports,
    error: reportError,
    refetch: refetchReports,
  } = ReportAPI.endpoints.getReports.useQuery(paramsReport);
  const reports = reportsResponse?.data || [];
  useWatchError(reportError);
  // Some API clients return undefined while loading
  // Following lines are here to prevent `rowCount` from being undefined during the loading
  // FIXME: change me later
  const rowCountRef = React.useRef(reportsResponse?.count || 0);

  const rowCount = React.useMemo(() => {
    if (reports?.length !== undefined) {
      rowCountRef.current = reportsResponse?.count || 0;
    }
    return rowCountRef.current;
  }, [reports]);

  const [reportSelected, setReportSelected] = useState<Report | null>(null);

  const columns: GridColDef<Report>[] = [
    {
      field: 'reportTitle',
      headerName: polyglot.t('reports.table.title') || 'Title',
      flex: 1,
      minWidth: 180,
      sortable: true,
    },
    {
      field: 'reportType',
      headerName: polyglot.t('reports.table.type') || 'Type',
      minWidth: 160,
      valueGetter: (params) => params.row.reportType?.name,
      sortable: true,
    },
    // {
    //   field: 'reportScheduleId',
    //   headerName: polyglot.t('reports.table.schedule') || 'Settings',
    //   minWidth: 140,
    //   sortable: true,
    // },
    {
      field: 'executionState',
      headerName: polyglot.t('reports.state') || 'State',
      sortable: true,
      minWidth: 120,
    },
    {
      field: 'createdAt',
      headerName: polyglot.t('general.created_at') || 'Created at',
      valueGetter: (params) => _.get(params.row, params.field, undefined),
      valueFormatter: (params) => {
        return params.value ? `${moment(params.value).format('L LT')}` : '';
      },
      minWidth: 200,
    },
    {
      field: 'timeRange.from',
      headerName: polyglot.t('reports.schedule.from') || 'From',
      valueGetter: (params) => _.get(params.row, params.field, undefined),
      valueFormatter: (params) =>
        params.value && `${moment(params.value).format('L')}`,
      minWidth: 120,
      sortable: false,
    },
    {
      field: 'timeRange.to',
      headerName: polyglot.t('reports.schedule.to') || 'to',
      valueGetter: (params) => _.get(params.row, params.field, undefined),
      valueFormatter: (params) =>
        params.value && `${moment(params.value).format('L')}`,
      minWidth: 120,
      sortable: false,
    },
    {
      field: 'formats',
      headerName: polyglot.t('reports.table.formats') || 'Formats',
      valueGetter: (params) =>
        Object.entries(params.row.files || {})
          .filter(([_, value]) => value)
          .map(([key]) => key)
          .join(', '),
      sortable: false,
    },
    {
      field: 'actions',
      headerName: polyglot.t('reports.table.actions') || 'Actions',
      renderCell: (params) => (
        <ReportMenuActions report={params.row} onSelect={setReportSelected} />
      ),
      minWidth: 100,
      sortable: false,
      filterable: false,
      disableColumnMenu: true,
    },
  ];

  return (
    <>
      <ReportFilters
        timeFrame={timeFrame}
        onChangeDatePicker={setTimeFrame}
        reportTypeIds={reportTypeIdsSelected}
        onChangeReportTypes={setReportTypeIdsSelected}
        refetchReports={refetchReports}
      />

      <DataGrid
        rows={reports ?? []}
        columns={columns}
        initialState={{
          pagination: {
            paginationModel: { page: 0, pageSize: 10 },
          },
          sorting: {
            sortModel: [{ field: 'createdAt', sort: 'desc' }],
          },
        }}
        rowCount={rowCount}
        loading={isFetchingReports}
        // FIXME: change me later when API supports server pagination
        // paginationModel={paginationModel}
        // paginationMode="server"
        // onPaginationModelChange={setPaginationModel}
        disableRowSelectionOnClick
        pageSizeOptions={[10, 25, 50, 100, 200, 500, 1000]}
      />
      <ReportDetailModal
        groupId={groupId}
        report={reportSelected ?? undefined}
        open={reportSelected !== null}
        onClose={() => setReportSelected(null)}
      />
    </>
  );
};

export default ReportsTable;
